import { createContext, useMemo, useRef } from 'react';

import { Menu } from 'primereact/menu';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useNavigate } from 'react-router-dom';

import { useAppVersion } from '@/core/version/AppVersionContext';
import { useCanUseV2 } from '@/modules/access/hooks/useCanUseV2';
import { buildMediaUrl } from '@shared/utils/mediaUrl';

import { useGetInsurance } from '../../../hooks/queries';
import { useTheme } from '../../../ThemeContext';

import './SideBar.css';

export const SideBarContext = createContext({
  isExpanded: true,
  setIsExpanded: () => {},
  isMobile: false,
});

/**
 *
 * @param {string} userName
 * @returns
 */
export default function SideBar({
  isExpandedSideBar,
  setIsExpandedSideBar,
  userName,
  isLoading,
  isMobile,
  children,
}) {
  const { isDark, toggleTheme } = useTheme();
  const { isV2, setVersion } = useAppVersion();
  const { canUseV2 } = useCanUseV2();
  const navigate = useNavigate();
  const settingsMenuRef = useRef(null);
  const insurance = useGetInsurance();
  const isLaMundial = insurance.data?.name?.trim().toLowerCase() === 'la mundial de seguros';
  const logoSrc = isLaMundial ? buildMediaUrl('img/ISOLOGO-la-mundial.png') : '/Isologo-fin.png';
  const logoAlt = isLaMundial ? 'La Mundial de seguros' : 'Citamed';

  const settingsMenuItems = useMemo(() => {
    const items = [
      {
        label: isDark ? 'Tema claro' : 'Tema oscuro',
        icon: `pi ${isDark ? 'pi-sun' : 'pi-moon'}`,
        command: () => toggleTheme(),
      },
    ];
    if (canUseV2) {
      items.push({
        label: isV2 ? 'Volver a versión anterior' : 'Nueva versión',
        icon: `pi ${isV2 ? 'pi-arrow-left' : 'pi-sparkles'}`,
        command: () => {
          setVersion(isV2 ? 'v1' : 'v2');
          window.location.reload();
        },
      });
    }
    return items;
  }, [isDark, toggleTheme, canUseV2, isV2, setVersion]);

  return (
    <SideBarContext.Provider
      value={{ isExpanded: isExpandedSideBar, setIsExpanded: setIsExpandedSideBar, isMobile }}
    >
      <aside className={`layout-sidebar ${isMobile && isExpandedSideBar ? 'mobile-open' : ''}`}>
        <header className={`sidebar-header ${isExpandedSideBar ? '' : 'minimized'}`}>
          <span className="sidebar-header-content">
            <div className="sidebar-logo">
              <img src={logoSrc} alt={logoAlt} />
            </div>
            {(isExpandedSideBar || isMobile) && <h2 className="sidebar-username">{userName}</h2>}
          </span>

          <button
            className={isExpandedSideBar ? 'sidebar-button-minimize' : 'sidebar-button-maximize'}
            onClick={() => setIsExpandedSideBar(!isExpandedSideBar)}
            type="button"
            aria-label="Toggle sidebar"
          >
            <i
              className={
                isMobile
                  ? 'pi pi-times'
                  : isExpandedSideBar
                    ? 'pi pi-chevron-left'
                    : 'pi pi-chevron-right'
              }
              aria-hidden="true"
            ></i>
          </button>
        </header>

        {isV2 && (
          <div className={`sidebar-beta-label${isExpandedSideBar || isMobile ? '' : ' collapsed'}`}>
            {isExpandedSideBar || isMobile ? 'Versión de Prueba' : 'BETA'}
          </div>
        )}

        <div className="sidebar-body">
          {isLoading ? (
            <div className="loading-container">
              <ProgressSpinner className="loading" />
            </div>
          ) : (
            <ul className={`sidebar-menu`}>{children}</ul>
          )}
        </div>

        <footer className={`sidebar-footer ${isExpandedSideBar ? '' : 'minimized'}`}>
          <Menu model={settingsMenuItems} popup ref={settingsMenuRef} id="sidebar_settings_menu" />
          <button
            type="button"
            className="sidebar-settings-btn"
            onClick={e => settingsMenuRef.current?.toggle(e)}
            aria-label="Ajustes"
            aria-haspopup
            aria-controls="sidebar_settings_menu"
            title="Ajustes"
          >
            <i className="pi pi-cog" aria-hidden="true" />
            <span className="sidebar-settings-btn__label">Ajustes</span>
          </button>

          <button
            type="button"
            className="sidebar-logout-btn"
            onClick={() => navigate('/logout')}
            aria-label="Cerrar sesión"
            title="Cerrar sesión"
          >
            <i className="pi pi-sign-out" aria-hidden="true" />
            <span className="sidebar-logout-btn__label">Cerrar sesión</span>
          </button>
        </footer>
      </aside>
    </SideBarContext.Provider>
  );
}
